import Container from '../../../../atoms/container/mosaicRailContainer';
import Direction from '../../../../../../shared/types/enums/direction';
import MainContext from '../../../../../../shared/components/utils/context/mainContext';
import Mosaic from '../../../mosaic/mosaic';
import MosaicCard from '../../../../elements/card/mosaicCard';
import RailProps from '../../../../../../shared/types/props/rail';
import RegularEvent from '../../../../../../shared/classes/regularEvent';
import goToContent from '../../../../../../shared/actions/content/goToContent';
import useList from '../../../../../../shared/components/utils/hooks/useList';
import useTransform from '../../../../../../shared/components/utils/hooks/useTransform';
import { useContext, useEffect, useRef, useState } from 'react';
import {
  routerSelector,
  updateParams,
} from '../../../../../../shared/store/slices/router.slice';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../../shared/store/hooks';

const MAX_COLS = 4;
const MIN_ROWS = 1;
const VERTICAL_OFFSET = 213;

function MosaicRail({ list }: RailProps) {
  const dispatch = useAppDispatch();
  const { data, focus, id } = list;
  const dataRef = useRef(data);
  dataRef.current = data;
  const { params } = useAppSelector(routerSelector);
  const { outOfBoundsAction, setAsset, rails } = useContext(MainContext);
  const { listIndex, setListIndex } = useList(id);
  const [mosaicIndex, setMosaicIndex] = useState(0);
  const [style, , , setStyle] = useTransform(
    Math.floor((params.current?.index as number) / MAX_COLS) ?? 0,
    VERTICAL_OFFSET,
    Direction.VERTICAL
  );

  // Actions

  const cardAction = (item: RegularEvent) => () => {
    dispatch(updateParams({ index: data.indexOf(item) }));
    goToContent(item);
  };

  // Event handlers

  const onChange = (index: number) => {
    setAsset(dataRef.current[index]);
    setMosaicIndex(index);
    setStyle(Math.floor(index / MAX_COLS));
  };

  const outOfBounds = (key: string | number) => {
    switch (key) {
      case 'LEFT':
      case 'RIGHT':
        outOfBoundsAction(-1, key.toString());
        break;
      case 'UP':
        if (rails?.length && id > 0) {
          setListIndex(id - 1);
        }
        break;
      case 'DOWN':
        if (rails?.length && id < rails?.length - 1) {
          setListIndex(id + 1);
        }
        break;
    }
  };

  // Effect hook

  useEffect(() => {
    // If two mosaics were to be displayed, this should change
    dispatch(
      updateParams({
        displayNextRails:
          listIndex === id
            ? data.length && (data.length - mosaicIndex) / MAX_COLS <= 2
            : undefined,
      })
    );
  }, [listIndex, mosaicIndex]);

  return (
    <Container animation={'rail-scroll'} style={style}>
      <Mosaic
        data={data}
        focus={focus}
        lowerBound={true}
        maxCols={MAX_COLS}
        stopPropagation={true}
        onChange={onChange}
        outOfBounds={outOfBounds}
      >
        {data?.map((item, index: number) => {
          if (!item) return null;
          return (
            <MosaicCard
              animatable={true}
              index={index}
              item={item as RegularEvent}
              key={index}
              maxCols={MAX_COLS}
              minRows={MIN_ROWS}
              action={cardAction(item as RegularEvent)}
              setAsset={setAsset}
            />
          );
        })}
      </Mosaic>
    </Container>
  );
}

export default MosaicRail;
